import * as THREE from "three";
import { isNil, isNotNil } from "es-toolkit";
import { ALL_MUSCLE_IDS, type MuscleId } from "@/entities/muscle/model/muscles";
import {
  EMISSIVE_STRENGTH,
  HIGHLIGHT_COLOR,
  SKIN_COLOR,
  SKIN_DIM_COLOR,
  type Highlight,
} from "@/entities/muscle/ui/limb-rigging.utils";
import { isMeshWithStandardMaterial } from "@/entities/muscle/ui/mesh-material.utils";

const colorDistance = (a: THREE.Color, b: THREE.Color): number =>
  Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b);

type HighlightTarget = Readonly<{
  color: THREE.Color;
  emissive: THREE.Color;
  opacity: number;
}>;

// Per-material animation target + a "seen once" set. First application snaps
// (no fade from the GLB's white material on load); later changes lerp toward
// the target in stepHighlights().
const TARGETS = new WeakMap<
  THREE.Material,
  { color: THREE.Color; emissive: THREE.Color; opacity: number }
>();
const INITIALIZED = new WeakSet<THREE.Material>();

// Close enough to snap and stop animating (sRGB channel + opacity epsilon).
const SETTLE_EPS = 0.004;

export class AnatomyScene {
  // Records where a mesh's material should end up. Snaps instantly the first
  // time (initial skin/highlight paint), so only user-driven changes animate.
  static setHighlightTarget({
    mesh,
    highlight,
    isGeneric,
    colorOverride,
  }: {
    mesh: THREE.Mesh;
    highlight: Highlight | undefined;
    isGeneric: boolean;
    colorOverride?: string;
  }): void {
    if (!isMeshWithStandardMaterial(mesh)) return;

    const material = mesh.material;
    let target = TARGETS.get(material);
    if (isNil(target)) {
      target = { color: new THREE.Color(), emissive: new THREE.Color(), opacity: 1 };
      TARGETS.set(material, target);
    }

    if (isNotNil(colorOverride)) {
      // Detail head accent, painted on top of the coarse muscle highlight.
      target.color.set(colorOverride);
      target.emissive.copy(target.color).multiplyScalar(0.6);
      target.opacity = 1;
    } else if (isNotNil(highlight)) {
      target.color.set(HIGHLIGHT_COLOR[highlight]);
      target.emissive.copy(target.color).multiplyScalar(EMISSIVE_STRENGTH[highlight]);
      target.opacity = 1;
    } else {
      target.color.set(isGeneric ? SKIN_DIM_COLOR : SKIN_COLOR);
      target.emissive.set("#000000");
      target.opacity = isGeneric ? 0.55 : 0.92;
    }

    if (!INITIALIZED.has(material)) {
      INITIALIZED.add(material);
      AnatomyScene.snapMaterial(material, target);
    }
  }

  private static snapMaterial(material: THREE.MeshStandardMaterial, target: HighlightTarget): void {
    material.color.copy(target.color);
    material.emissive.copy(target.emissive);
    material.opacity = target.opacity;
    material.transparent = material.opacity < 1;
    material.needsUpdate = true;
  }

  // Lerps one mesh toward its target. Returns true while still animating so the
  // caller can keep requesting frames; false once settled (snapped to target).
  static stepHighlight(mesh: THREE.Mesh, alpha: number): boolean {
    if (!isMeshWithStandardMaterial(mesh)) return false;

    const material = mesh.material;
    const target = TARGETS.get(material);
    if (isNil(target)) return false;

    material.color.lerp(target.color, alpha);
    material.emissive.lerp(target.emissive, alpha);
    material.opacity += (target.opacity - material.opacity) * alpha;

    const hasSettled =
      colorDistance(material.color, target.color) < SETTLE_EPS &&
      Math.abs(material.opacity - target.opacity) < SETTLE_EPS;

    if (hasSettled) AnatomyScene.snapMaterial(material, target);
    else {
      material.transparent = true;
      material.needsUpdate = true;
    }

    return !hasSettled;
  }

  static computePrimaryBoundingBox(
    muscleMeshes: ReadonlyMap<MuscleId, ReadonlyArray<THREE.Mesh>>,
    active: Partial<Record<MuscleId, Highlight>>,
  ): THREE.Box3 | null {
    const aggregateBox = new THREE.Box3();
    let hasAny = false;
    for (const [muscleId, meshes] of muscleMeshes) {
      if (active[muscleId] !== "primary") continue;

      for (const mesh of meshes) {
        const meshBox = new THREE.Box3().setFromObject(mesh);
        if (!meshBox.isEmpty()) {
          aggregateBox.union(meshBox);
          hasAny = true;
        }
      }
    }

    return hasAny ? aggregateBox : null;
  }

  static buildMuscleLabels(
    muscleLabels: Partial<Record<MuscleId, string>> | undefined,
    muscleCenters: Partial<Record<MuscleId, [number, number, number]>>,
  ): ReadonlyArray<readonly [MuscleId, string]> {
    if (isNil(muscleLabels)) return [];

    return ALL_MUSCLE_IDS.flatMap((muscleId) => {
      const text = muscleLabels[muscleId];
      const center = muscleCenters[muscleId];

      return isNotNil(text) && isNotNil(center) ? [[muscleId, text] as const] : [];
    });
  }
}
